import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import { IFetchResponse } from '~utilities/fetch';
import {
    IAbaGetAction,
    IAbaHistoriesListAction,
    IAbaTransactionsListAction,
    IAbasAddAction,
    IAbasListAction,
    IUnallocatedTransactionsListAction,
    abaHistoriesSetAction,
    abaSetAction,
    abaTransactionsSetAction,
    abasSetAction,
    unallocatedTransactionsSetAction,
} from './actions';
import WarehouseLoanTransactionAbasActionsEnum from './ActionsEnum';
import {
    abaGetRequest,
    abaHistoriesListRequest,
    abaTransactionsListRequest,
    abasAddRequest,
    abasListRequest,
    unallocatedTransactionsListRequest,
} from '~Api/WarehouseLoanTransactionAba/requests';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { parseWarehouseLoanTransaction } from '~Api/Warehouse/parsers';
import { notification } from 'antd';
import { renderNotificationLoadingIcon } from '~utilities/utils';
import IWarehouseLoanTransactionAba from '~Api/WarehouseLoanTransactionAba/IWarehouseLoanTransactionAba';
import {
    parseWarehouseLoanTransactionAba,
    parsewarehouseLoanTransactionAbaHistory,
} from '~Api/WarehouseLoanTransactionAba/parsers';
import _ from 'lodash';
import IWarehouseLoanTransactionAbaHistory from '~Api/WarehouseLoanTransactionAba/IWarehouseLoanTransactionAbaHistory';
import AbaTypeEnum from '~Api/WarehouseLoanTransactionAba/AbaTypeEnum';
import AbaTypeGroupEnum from './AbaTypeGroupEnum';

function* abasAdd(action: IAbasAddAction): Iterator<unknown> {
    const key: string = `abasAdd ${action.abaType}`;
    const message: string = 'Add Prepaid Interest Sale Transfer';

    notification.open({
        description: 'Adding prepaid interest sale transfer...',
        duration: 0,
        icon: renderNotificationLoadingIcon(),
        key,
        message,
    });

    const abasAddResponse: IFetchResponse = yield call(abasAddRequest, action.abaType, action.transactionUuids);
    if (abasAddResponse.status === 422) {
        notification.error({
            description: `There was a problem adding the prepaid interest sale transfer: ${_.values(abasAddResponse.body)[0]}.`,
            duration: 0,
            key,
            message,
        });
    } else {
        const aba: IWarehouseLoanTransactionAba = yield call(parseWarehouseLoanTransactionAba, abasAddResponse.body);
        yield put(abaSetAction(aba));

        notification.success({
            description: 'The prepaid interest sale transfer has been added.',
            duration: 4.5,
            key,
            message,
        });
    }
}

function* abasList(action: IAbasListAction): Iterator<unknown> {
    let abaTypes: AbaTypeEnum[];
    switch (action.abaTypeGroup) {
        case AbaTypeGroupEnum.PrefundPrepaidInterest:
            abaTypes = [
                AbaTypeEnum.Fwt1PrefundPrepaidInterest,
                AbaTypeEnum.Fwt2PrefundPrepaidInterest,
            ];
            break;
        case AbaTypeGroupEnum.RepaymentPrincipal:
            abaTypes = [
                AbaTypeEnum.Fwt1RepaymentPrincipal,
                AbaTypeEnum.Fwt2RepaymentPrincipal,
            ];
            break;
        case AbaTypeGroupEnum.SellInPrepaidInterest:
            abaTypes = [
                AbaTypeEnum.Fwt1SellInPrepaidInterest,
                AbaTypeEnum.Fwt2SellInPrepaidInterest,
            ];
            break;
    }

    const abasListResponse: IFetchResponse = yield call(abasListRequest, abaTypes);
    const abas: IWarehouseLoanTransactionAba[] = yield Promise.all(abasListResponse.body.map(parseWarehouseLoanTransactionAba));
    yield put(abasSetAction(action.abaTypeGroup, abas));
}

function* abaGet(action: IAbaGetAction): Iterator<unknown> {
    const abaGetResponse: IFetchResponse = yield call(abaGetRequest, action.uuid);
    const aba: IWarehouseLoanTransactionAba = yield parseWarehouseLoanTransactionAba(abaGetResponse.body);
    yield put(abaSetAction(aba));
}

function* abaHistoriesList(action: IAbaHistoriesListAction): Iterator<unknown> {
    const historiesListResponse: IFetchResponse = yield call(abaHistoriesListRequest, action.uuid);
    const histories: IWarehouseLoanTransactionAbaHistory[] = yield Promise.all(historiesListResponse.body.map(parsewarehouseLoanTransactionAbaHistory));
    yield put(abaHistoriesSetAction(action.uuid, histories));
}

function* abaTransactionsList(action: IAbaTransactionsListAction): Iterator<unknown> {
    const transactionsListResponse: IFetchResponse = yield call(abaTransactionsListRequest, action.uuid);
    const transactions: IWarehouseLoanTransaction[] = yield Promise.all(transactionsListResponse.body.map(parseWarehouseLoanTransaction));
    yield put(abaTransactionsSetAction(action.uuid, transactions));
}

function* unallocatedTransactionsList(action: IUnallocatedTransactionsListAction): Iterator<unknown> {
    const unallocatedTransactionsResponse: IFetchResponse = yield call(unallocatedTransactionsListRequest, action.abaType);
    const unallocatedTransactions: IWarehouseLoanTransaction[] = yield Promise.all(unallocatedTransactionsResponse.body.map(parseWarehouseLoanTransaction));
    yield put(unallocatedTransactionsSetAction(action.abaType, unallocatedTransactions));
}

export function* WarehouseLoanTransactionAbasSagas(): Iterator<unknown> {
    yield all([
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.AbasAdd, abasAdd),
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.AbasList, abasList),
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.AbaHistoriesList, abaHistoriesList),
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.AbaGet, abaGet),
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.AbaTransactionsList, abaTransactionsList),
        takeEvery(WarehouseLoanTransactionAbasActionsEnum.UnallocatedTransactionsList, unallocatedTransactionsList),
    ]);
}
